import React, { useEffect, useMemo, useRef, useState } from 'react';

import useSittingStore from '../../stores/sittingStore';

/**
 * ProcedureNoteDialog — Phase 4.0i chat-style thread on a sitting.
 *
 * Replaces the old visit-metadata-based dialog. Reads + writes go
 * through the new ProcedureNote endpoints (sittingStore actions
 * `fetchSittingNotes` + `addSittingNote`). The store's
 * `notesBySitting[sitting_id]` projection is the source of truth and
 * stays live via the SITTING_NOTE_ADDED WS handler.
 *
 * Visual model: messenger-style bubbles. Viewer's own messages
 * right-aligned; everyone else's left-aligned. Author tint by role
 * (teal=doctor, blue=nurse, slate=admin). Newest at the bottom; auto-
 * scroll on new arrivals.
 *
 * Props:
 *   sitting   — { plan_id, sitting_id, patient_name, procedure_name }
 *   viewer    — { memberId, role }
 *   onClose   — () => void
 *   onSaved   — () => void  (legacy hook; called once per successful post)
 *
 * The dialog is tagged `data-keep-bubble-open="1"` on its outermost
 * node so a click inside it doesn't trigger the click-outside collapse
 * on any parent procedure pill.
 */

const ROLE_TINT = {
  DOCTOR: { own: '#0d9488', other: '#0f766e' },     // teal
  NURSE:  { own: '#2563eb', other: '#1d4ed8' },     // blue
  ADMIN:  { own: '#475569', other: '#334155' },     // slate
};

// Hoisted empty fallback so the selector returns a stable reference when
// the thread hasn't been hydrated yet. Returning a fresh `[]` from the
// selector triggered useSyncExternalStore's "state changed" path on
// every render → infinite re-render loop.
const EMPTY_NOTES = Object.freeze([]);

const TERMINAL_STATUSES = new Set(['completed', 'paid', 'cancelled']);

function formatClock(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const time = d.toLocaleTimeString('en-IN', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
    // Compose a date prefix that reads naturally:
    //   - Today        → just the time (e.g. "5:30 PM")
    //   - Yesterday    → "Yesterday, 5:30 PM"
    //   - Older        → "30 Apr, 5:30 PM" (day + short month + time)
    // Notes that originated days ago need the date for the reader to
    // place the message in context; today's notes don't, so we keep
    // the chat-like brevity for the common case.
    const today = new Date();
    const yest = new Date(today);
    yest.setDate(today.getDate() - 1);
    const sameDay = (a, b) => (
      a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate()
    );
    if (sameDay(d, today)) return time;
    if (sameDay(d, yest)) return `Yesterday, ${time}`;
    const datePart = d.toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short',
    });
    return `${datePart}, ${time}`;
  } catch {
    return '';
  }
}

function bubbleColor(role, isOwn) {
  const r = (role || '').toUpperCase();
  const t = ROLE_TINT[r] || ROLE_TINT.DOCTOR;
  return isOwn ? t.own : t.other;
}

export default function ProcedureNoteDialog({ sitting, viewer, onClose, onSaved }) {
  const isTerminal = TERMINAL_STATUSES.has(sitting?.status);
  const planId = sitting?.plan_id;
  const sittingId = sitting?.sitting_id;
  const myMemberId = viewer?.memberId;

  const notes = useSittingStore(
    (s) => (sittingId ? (s.notesBySitting?.[sittingId] || EMPTY_NOTES) : EMPTY_NOTES),
  );
  const fetchSittingNotes = useSittingStore((s) => s.fetchSittingNotes);
  const addSittingNote = useSittingStore((s) => s.addSittingNote);

  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loadedOnce, setLoadedOnce] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // Tracks where the current click sequence originated. Without this,
  // dragging the textarea resize grip from inside the panel out onto
  // the backdrop generates a click event whose target is the backdrop
  // itself — which would fire the close handler and dismiss the
  // dialog mid-drag. We only treat the close as legitimate when both
  // mousedown AND mouseup landed on the backdrop.
  const backdropDownTargetRef = useRef(null);

  // Initial fetch on mount (and when sitting_id changes). VisitProceduresPanel
  // eagerly hydrates the cache via fetchVisitNotes — a single GSI9 Query for
  // the whole visit costs the same RCU as the per-sitting Query, so when the
  // cache is already warm we skip the network call and rely on the WS handler
  // (SITTING_NOTE_ADDED) to keep the thread live.
  useEffect(() => {
    if (!planId || !sittingId) return;
    const cached = useSittingStore.getState().notesBySitting?.[sittingId];
    if (cached && cached.length > 0) {
      setLoadedOnce(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const r = await fetchSittingNotes(planId, sittingId);
      if (cancelled) return;
      if (!r?.success) setError(r?.error || 'Could not load notes.');
      setLoadedOnce(true);
    })();
    return () => { cancelled = true; };
  }, [planId, sittingId, fetchSittingNotes]);

  // Auto-scroll on new arrivals.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [notes.length]);

  // Focus the input when the dialog opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const title = useMemo(() => {
    const patient = sitting?.patient_name || '';
    const proc = sitting?.procedure_name || 'Procedure';
    return patient ? `${proc} · ${patient}` : proc;
  }, [sitting]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const trimmed = body.trim();
    if (!trimmed || busy) return;
    if (trimmed.length > 4000) {
      setError('Note is too long (max 4000 characters).');
      return;
    }
    setBusy(true);
    setError('');
    const r = await addSittingNote(planId, sittingId, trimmed);
    setBusy(false);
    if (r?.success) {
      setBody('');
      onSaved?.();
      // Refocus for the next message.
      inputRef.current?.focus();
    } else {
      setError(r?.error || 'Could not send note.');
    }
  };

  const handleKey = (e) => {
    // Cmd/Ctrl + Enter sends; plain Enter inserts newline so multi-line
    // dictation feels natural.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Notes for ${title}`}
      data-keep-bubble-open="1"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onMouseDown={(e) => {
        backdropDownTargetRef.current = e.target;
      }}
      onTouchStart={(e) => {
        backdropDownTargetRef.current = e.target;
      }}
      onClick={(e) => {
        // Only dismiss when the entire click started AND ended on the
        // backdrop. Drags that originated on the textarea (resize grip
        // or scrollbar) shouldn't be interpreted as "click outside".
        if (e.target === e.currentTarget
            && backdropDownTargetRef.current === e.currentTarget) {
          onClose?.();
        }
        backdropDownTargetRef.current = null;
      }}
    >
      <div
        className="bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100"
        style={{
          width: 'min(540px, 100%)',
          maxHeight: '80vh',
          borderRadius: 14,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <header
          className="bg-slate-50 dark:bg-slate-800"
          style={{
            padding: '0.875rem 1.125rem',
            borderBottom: '1px solid rgba(15,23,42,0.10)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: '1.125rem' }}>📝</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.9375rem', fontWeight: 700 }}>Notes</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title}
            </div>
            {isTerminal && (
              <div
                className="text-slate-600 dark:text-slate-400"
                style={{ fontSize: '0.625rem', fontWeight: 600, marginTop: 2 }}
              >
                ✓ Procedure {sitting?.status} — replies still allowed
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="px-2.5 py-1 rounded-md border bg-white text-slate-700 hover:bg-slate-100
                       dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
            style={{ fontSize: '0.8125rem', borderColor: '#cbd5e1' }}
          >
            ✕
          </button>
        </header>

        {/* Thread body */}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            background: 'transparent',
          }}
        >
          {!loadedOnce && (
            <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7, textAlign: 'center' }}>
              Loading thread…
            </p>
          )}

          {loadedOnce && notes.length === 0 && (
            <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7, textAlign: 'center', padding: '1rem 0' }}>
              No notes yet. Start the conversation — anyone involved with this procedure can post.
            </p>
          )}

          {notes.map((n) => {
            const isOwn = !!myMemberId && n.author?.member_id === myMemberId;
            const tint = bubbleColor(n.author?.role, isOwn);
            const author = n.author || {};
            const roleLabel = author.role
              ? author.role.charAt(0).toUpperCase() + author.role.slice(1).toLowerCase()
              : '';
            return (
              <div
                key={n.note_id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isOwn ? 'flex-end' : 'flex-start',
                }}
              >
                <div style={{ fontSize: '0.625rem', opacity: 0.7, padding: '0 0.25rem' }}>
                  {isOwn ? 'You' : (author.name || 'Unknown')}
                  {roleLabel && <> · {roleLabel}</>}
                  {n.created_at && <> · {formatClock(n.created_at)}</>}
                </div>
                <div
                  style={{
                    maxWidth: '78%',
                    marginTop: 2,
                    padding: '0.5rem 0.75rem',
                    borderRadius: 14,
                    background: tint,
                    color: '#ffffff',
                    fontSize: '0.8125rem',
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.10)',
                  }}
                >
                  {n.body}
                </div>
              </div>
            );
          })}
        </div>

        {/* Composer */}
        <form
          onSubmit={handleSend}
          className="bg-slate-50 dark:bg-slate-800"
          style={{
            borderTop: '1px solid rgba(15,23,42,0.10)',
            padding: '0.625rem 0.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {error && (
            <p style={{ margin: 0, fontSize: '0.6875rem', color: '#b91c1c' }}>
              {error}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
            <textarea
              ref={inputRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Add a note for the team…"
              rows={1}
              maxLength={4000}
              className="bg-white text-slate-900 border-slate-300
                         dark:bg-slate-900 dark:text-slate-100 dark:border-slate-600"
              style={{
                flex: 1,
                resize: 'vertical',
                minHeight: 38,
                maxHeight: 160,
                padding: '0.5rem 0.625rem',
                borderRadius: 8,
                borderWidth: 1,
                borderStyle: 'solid',
                fontSize: '0.8125rem',
                fontFamily: 'inherit',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={busy || !body.trim()}
              className="px-3 py-2 rounded-md bg-teal-600 text-white text-sm font-semibold
                         hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed
                         dark:bg-teal-500 dark:hover:bg-teal-400 dark:text-slate-900"
            >
              {busy ? 'Sending…' : 'Send'}
            </button>
          </div>
          <p style={{ margin: 0, fontSize: '0.625rem', opacity: 0.6 }}>
            {body.length}/4000 · ⌘/Ctrl + Enter to send
          </p>
        </form>
      </div>
    </div>
  );
}
